/**
 * apps/web :: components :: ErrorBoundary
 *
 * Wave 14k (ROADS-348) — top-level React error boundary.
 *
 * Catches render-phase + lifecycle exceptions thrown anywhere inside the
 * router tree and:
 *   - reports the error + componentStack to `@/lib/error-reporter`, and
 *   - renders a friendly i18n-keyed fallback ("Something went wrong. The
 *     team has been notified.").
 *
 * The fallback is intentionally minimal — no retry button, no stack
 * surface to the user. A full reload (`window.location.reload()`) is the
 * cheapest path back to a known-good state and the link does that
 * explicitly.
 */
import React from "react";
import { withTranslation } from "react-i18next";
import type { WithTranslation } from "react-i18next";

import { report } from "@/lib/error-reporter";

interface Props extends WithTranslation {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
}

class ErrorBoundaryInner extends React.Component<Props, State> {
  public state: State = { hasError: false };

  public static getDerivedStateFromError(_error: Error): State {
    return { hasError: true };
  }

  public componentDidCatch(
    error: Error,
    info: React.ErrorInfo,
  ): void {
    // Best-effort: the reporter swallows its own failures, so we can call
    // it unconditionally without risk of cascading.
    try {
      report(error, info.componentStack ?? null);
    } catch {
      // intentional swallow — never let the boundary re-throw
    }
  }

  public render(): React.ReactNode {
    if (this.state.hasError) {
      const { t } = this.props;
      return (
        <div
          role="alert"
          className="mx-auto w-full max-w-2xl px-md py-lg text-center"
        >
          <h1 className="text-xl font-semibold mb-sm">
            {t("errorBoundary.title")}
          </h1>
          <p className="text-base mb-md">{t("errorBoundary.body")}</p>
          <p className="text-sm">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="underline cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
            >
              {t("errorBoundary.reload")}
            </button>
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

const ErrorBoundary = withTranslation()(ErrorBoundaryInner);
export default ErrorBoundary;
